/**
 * stopUpdates — the ParallelCall fields written when a call is ended by the
 * caller (Stop, hang up and continue). RUI-P1/P1b: the old Stop path wrote
 * `call_outcome: 'not_reachable'` and `call_notes: 'Arret manuel'` on every
 * active call, erasing the notes and the outcome of a call that reached a
 * human. Existing notes and outcomes are never overwritten; a fallback
 * outcome is only written when the line never reached a human.
 */
export interface StoppableCall {
  id: string;
  call_status?: string | null;
  call_outcome?: string | null;
  call_notes?: string | null;
  connected_at?: string | null;
}

const HUMAN_STATUSES = ['connected_to_agent', 'human_detected'];

export function isAudibleCall(call: StoppableCall): boolean {
  return HUMAN_STATUSES.includes(call.call_status || '') || !!call.connected_at;
}

export function stopUpdatesFor(call: StoppableCall, nowIso: string, defaultNote: string): Record<string, string> {
  const isVoicemail = call.call_status === 'voicemail' || call.call_outcome === 'voicemail' || call.call_outcome === 'left_voicemail';
  const hasHumanSignal = !isVoicemail && isAudibleCall(call);
  let fallbackOutcome: string | null;
  if (isVoicemail) fallbackOutcome = 'no_answer';
  else if (hasHumanSignal) fallbackOutcome = null;
  else fallbackOutcome = 'not_reachable';
  const updates: Record<string, string> = { call_status: 'completed', ended_at: nowIso };
  // A call that reached a human: its notes are the agent's. The snapshot read
  // here can predate a note typed in the panel, so never write them at all.
  if (!hasHumanSignal && !call.call_notes) updates.call_notes = defaultNote;
  const outcome = call.call_outcome || fallbackOutcome;
  if (outcome && !call.call_outcome) updates.call_outcome = outcome;
  return updates;
}
